import { CheckCircleTwoTone, ExpandMore, RemoveCircleTwoTone } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Collapse, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { computed } from '@preact/signals-react';
import { t } from 'i18next';
import { useRef } from 'react';
import { RuleCategory, getRuleCategoryLabel } from '../../../../common/enums/RuleCategory';
import { getTraceTypeLabel } from '../../../../common/enums/TraceType';
import { useTabbable } from '../../../../common/hooks/useTabbable';
import { RuleSummary } from '../../common/components/RuleSummary';
import { RuleToggle } from '../../common/components/RuleToggle';
import { CATEGORY_ACCORDION_SX } from '../../common/styles/accordion';
import { TRACER_CONSTANTS } from '../../common/styles/constants';
import { PROFILE_ROOT_STYLES } from '../../common/styles/root';
import { CERT_EXPIRING_RULE, ThresholdRule } from '../../models/Threshold';
import { TracerRule } from '../../models/TracerRule';
import { TracerStore } from '../../store/TracerStore';
import { ExpiringSoonDisplay } from './ExpiringSoonDisplay';
import { ThresholdDisplay } from './ThresholdDisplay';

type TracerGroupProps = {
  rules: TracerRule[];
  category: RuleCategory;
};

type TracerEntryProps = {
  rule: TracerRule;
};

type TracerEntrySummaryProps = {
  rule: TracerRule;
};

const TracerRuleEntrySummary = ({ rule }: TracerEntrySummaryProps) => {
  const store = useRef(TracerStore.getInstance()).current;
  const availableTraceTypes = store.getAvailableTraceTypes();

  const [boxRef, onKeyDown] = useTabbable();

  return (
    <Stack ref={boxRef} onKeyDown={onKeyDown} className="profile-row">
      <RuleSummary number={rule.getNumber()} title={rule.getTitle()} subtitle={rule.getShortExplanation()} />
      <RuleToggle ruleNumber={rule.getNumber()} store={store} />
      {availableTraceTypes.map((tt) => {
        const compatible = rule.getCompatibleTraceTypes().has(tt);
        const tooltip = t(`a11y.profile.${compatible ? 'compatibleTracerRule' : 'incompatibleTracerRule'}`, { traceType: getTraceTypeLabel(tt) });
        const dataRole = `trace-type${!compatible ? '-not' : ''}-available-${rule.getNumber()}-${tt}`;
        const iconProps = { tabIndex: -1, 'data-role': 'tabbable', role: 'img' };
        const icon = compatible ? <CheckCircleTwoTone color="success" {...iconProps} /> : <RemoveCircleTwoTone color="error" {...iconProps} />;
        return (
          <Box key={tt} className="profile-column profile-content-column trace-type-icon" data-role={dataRole}>
            <Tooltip title={tooltip}>{icon}</Tooltip>
          </Box>
        );
      })}
    </Stack>
  );
};

const TracerRuleEntry = ({ rule }: TracerEntryProps) => {
  const store = useRef(TracerStore.getInstance()).current;
  const disabledRules = store.getDisabledRules();
  const isEditable = store.isEditable();
  const ruleNumber = rule.getNumber();

  const enabledInProfile = computed(() => {
    return !disabledRules.has(ruleNumber);
  });
  const renderExpiringSoon = ruleNumber === CERT_EXPIRING_RULE;
  const hideRule202Threshold = ruleNumber === (ThresholdRule.STALE_KEY_202 as number) && store.isRule203Enabled();

  const list = store.getCardList(ruleNumber);

  const renderCards = list !== null && !hideRule202Threshold;

  // If it is `rendered`, the collapsible box is expanded if the rule is currently enabled,
  // collapsed otherwise. This guarantees that input values are not erased upon expansion, and
  // preserves the expand animation for all rules except 202-203
  const collapseProps = {
    tabIndex: isEditable ? -1 : 0,
    in: enabledInProfile.value,
    unmountOnExit: false,
    role: 'presentation',
    className: 'tracer-collapse-panel',
  };

  return (
    <Stack role="listitem" className="border-bottom">
      <TracerRuleEntrySummary rule={rule} />
      {renderCards && (
        <Collapse {...collapseProps} data-role={`threshold-${ruleNumber}`}>
          <ThresholdDisplay list={list} ruleNumber={ruleNumber} />
        </Collapse>
      )}
      {renderExpiringSoon && (
        <Collapse {...collapseProps} data-role="expiring-soon">
          <ExpiringSoonDisplay />
        </Collapse>
      )}
    </Stack>
  );
};

const TracerGroupEntry = ({ rules, category, ...rest }: TracerGroupProps) => {
  return (
    <Accordion disableGutters defaultExpanded>
      <AccordionSummary
        id={`rule-group-category-${category}-header`}
        aria-controls={`rule-group-category-${category}-content`}
        expandIcon={<ExpandMore />}
        title={t('a11y.profile.tracerCategoryAccordion', { category: getRuleCategoryLabel(category) })}
        className="border-bottom"
      >
        {getRuleCategoryLabel(category)}
      </AccordionSummary>
      <AccordionDetails role="list">
        {rules.map((rule) => (
          <TracerRuleEntry key={rule.getNumber()} rule={rule} {...rest} />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

export const TracerTable = () => {
  const store = useRef(TracerStore.getInstance()).current;
  const availableTraceTypes = store.getAvailableTraceTypes();
  const ruleGroups = store.getRuleGroups();
  // The rule column should occupy 40% of the available space
  // Other types will expand as they can: the `status` column is included in the count
  const columnWidth = 60 / (availableTraceTypes.length + 1);
  const { MIN_TABLE_WIDTH, HEADER_HEIGHT, RULE_STICKY_TOP, THRESHOLD_INPUT_STICKY_TOP, ACCORDION_STICKY_TOP, THRESHOLD_BOX_STICKY_TOP } = TRACER_CONSTANTS;

  return (
    <Box
      data-role="tracer-table"
      component={Paper}
      sx={deepmerge(PROFILE_ROOT_STYLES, {
        overflow: { xs: 'auto', md: 'unset' },
        ...CATEGORY_ACCORDION_SX({ stickyOffset: ACCORDION_STICKY_TOP }),
        '& .profile-rule-summary': { scrollMarginTop: RULE_STICKY_TOP },
        '& .tracer-collapse-panel': { scrollMarginTop: THRESHOLD_BOX_STICKY_TOP },
        '& .tracer-input-field input': { scrollMarginTop: THRESHOLD_INPUT_STICKY_TOP },
        '& .profile-header': { minWidth: MIN_TABLE_WIDTH },
        '& .MuiAccordion-root': { width: 1, minWidth: MIN_TABLE_WIDTH },
        '& .profile-column': {
          flexGrow: 0,
          flexShrink: 0,
          flexBasis: `max(${columnWidth}%, 4rem)`,
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
          flexWrap: 'wrap',
          px: 0.5,
          '&.profile-content-column': {
            display: 'flex',
            justifyContent: 'center',
          },
        },
      })}
    >
      <Stack className="border-bottom profile-header" sx={{ height: HEADER_HEIGHT, position: 'sticky', top: 0, zIndex: 3 }}>
        <Typography sx={{ flexGrow: 1 }}>{t('rule')}</Typography>
        <Typography className="profile-column">{t('status')}</Typography>
        {availableTraceTypes.map((t) => (
          <Typography key={t} className="profile-column">
            {getTraceTypeLabel(t)}
          </Typography>
        ))}
      </Stack>
      {Array.from(ruleGroups.entries()).map(([category, rules]) => (
        <TracerGroupEntry key={category} rules={rules} category={category} />
      ))}
    </Box>
  );
};
